import { useLayoutEffect, useRef, useState } from 'react';
import {
  Bold,
  Code,
  Heading1,
  Heading2,
  Italic,
  Link,
  List,
  ListOrdered,
  Quote,
  Strikethrough,
} from 'lucide-react';

import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { SegmentedToggle } from '@/components/pages/calendar/facet-filter';
import { MessageBodyMarkdown } from '@/components/messages/message-body-markdown';
import { cn } from '@/lib/utils';
import {
  insertLink,
  toggleInlineWrap,
  toggleLinePrefix,
  toggleOrderedList,
} from '@/lib/markdown/markdown-commands';

const MODES = [
  { id: 'write', label: 'Write' },
  { id: 'preview', label: 'Preview' },
];

// Heading levels replace each other rather than stacking (see toggleLinePrefix's `siblings`).
const HEADINGS = ['# ', '## ', '### '];

const TOOLBAR = [
  { id: 'bold', icon: Bold, title: 'Bold', shortcut: 'b', run: (t, s, e) => toggleInlineWrap(t, s, e, '**', 'bold text') },
  { id: 'italic', icon: Italic, title: 'Italic', shortcut: 'i', run: (t, s, e) => toggleInlineWrap(t, s, e, '*', 'italic text') },
  { id: 'strike', icon: Strikethrough, title: 'Strikethrough', run: (t, s, e) => toggleInlineWrap(t, s, e, '~~', 'struck text') },
  { divider: true, id: 'd1' },
  { id: 'h1', icon: Heading1, title: 'Large heading', run: (t, s, e) => toggleLinePrefix(t, s, e, '# ', HEADINGS) },
  { id: 'h2', icon: Heading2, title: 'Small heading', run: (t, s, e) => toggleLinePrefix(t, s, e, '## ', HEADINGS) },
  { divider: true, id: 'd2' },
  { id: 'ul', icon: List, title: 'Bulleted list', run: (t, s, e) => toggleLinePrefix(t, s, e, '- ', ['> ']) },
  { id: 'ol', icon: ListOrdered, title: 'Numbered list', run: toggleOrderedList },
  { id: 'quote', icon: Quote, title: 'Quote', run: (t, s, e) => toggleLinePrefix(t, s, e, '> ', ['- ']) },
  { divider: true, id: 'd3' },
  { id: 'link', icon: Link, title: 'Link', shortcut: 'k', run: insertLink },
  { id: 'code', icon: Code, title: 'Inline code', run: (t, s, e) => toggleInlineWrap(t, s, e, '`', 'code') },
];

/**
 * Markdown textarea with a formatting toolbar and a preview toggle. The preview renders through the
 * same pipeline as chat messages (MessageBodyMarkdown), so what an author previews is what everyone
 * else will read.
 *
 * Safe by construction: react-markdown renders no raw HTML unless rehype-raw is added, and it
 * sanitizes link protocols — so pasted <script> or javascript: URLs are inert. Do not add rehype-raw.
 */
export function MarkdownEditor({
  value,
  onChange,
  maxLength = 8000,
  rows = 8,
  disabled = false,
  placeholder,
  previewMaxHeightClassName = 'max-h-64',
  // The textarea has a min-height of its own, so `rows` alone cannot make it shorter. Callers
  // that want a compact box (the announcement comment composer) override it here.
  minHeightClassName = 'min-h-32',
  // Surface override for callers whose own background already IS `background` — the comment
  // composer sits inside an announcement card and would otherwise disappear into it.
  className,
}) {
  const [mode, setMode] = useState('write');
  const textareaRef = useRef(null);
  // The textarea is controlled, so React re-renders with the new value and drops the caret to the
  // end. Stash the selection a command wants and restore it once that value has painted.
  const pendingSelectionRef = useRef(null);
  const text = String(value || '');
  const isNearLimit = text.length > maxLength * 0.9;

  useLayoutEffect(() => {
    const pending = pendingSelectionRef.current;
    if (!pending || !textareaRef.current) return;
    pendingSelectionRef.current = null;
    textareaRef.current.focus();
    textareaRef.current.setSelectionRange(pending.start, pending.end);
  }, [value]);

  const runCommand = (command) => {
    const el = textareaRef.current;
    if (!el || disabled) return;
    const result = command.run(text, el.selectionStart, el.selectionEnd);
    // Formatting adds characters; the textarea's own maxLength only constrains typing.
    if (result.text.length > maxLength) return;
    pendingSelectionRef.current = { start: result.start, end: result.end };
    onChange?.(result.text);
    if (mode !== 'write') setMode('write');
  };

  const handleKeyDown = (event) => {
    if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
    const command = TOOLBAR.find((item) => item.shortcut && item.shortcut === event.key.toLowerCase());
    if (!command) return;
    event.preventDefault();
    runCommand(command);
  };

  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border border-input bg-background',
        disabled && 'opacity-60',
        className,
      )}
    >
      <div className="flex items-center gap-2 border-b border-border/60 bg-muted/30 px-2 py-1.5">
        <SegmentedToggle value={mode} segments={MODES} onChange={setMode} ariaLabel="Editor mode" />
        <span
          className={cn(
            'ml-auto pr-1 text-[11px] tabular-nums',
            isNearLimit ? 'font-medium text-amber-600 dark:text-amber-400' : 'text-muted-foreground',
          )}
        >
          {text.length.toLocaleString()} / {maxLength.toLocaleString()}
        </span>
      </div>

      {mode === 'write' ? (
        <>
          <div
            role="toolbar"
            aria-label="Formatting"
            aria-controls="markdown-editor-textarea"
            className="flex flex-wrap items-center gap-0.5 border-b border-border/60 px-1.5 py-1"
          >
            {TOOLBAR.map((item) =>
              item.divider ? (
                <span key={item.id} className="mx-1 h-4 w-px shrink-0 bg-border" aria-hidden />
              ) : (
                <button
                  key={item.id}
                  type="button"
                  disabled={disabled}
                  title={item.shortcut ? `${item.title} (⌘${item.shortcut.toUpperCase()})` : item.title}
                  aria-label={item.title}
                  // onMouseDown, not onClick: the default mousedown would blur the textarea and
                  // discard the selection the command needs to read.
                  onMouseDown={(event) => {
                    event.preventDefault();
                    runCommand(item);
                  }}
                  className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
                >
                  <item.icon className="h-3.5 w-3.5" aria-hidden />
                </button>
              ),
            )}
          </div>
          <Textarea
            id="markdown-editor-textarea"
            ref={textareaRef}
            value={text}
            onChange={(event) => onChange?.(event.target.value)}
            onKeyDown={handleKeyDown}
            rows={rows}
            maxLength={maxLength}
            disabled={disabled}
            placeholder={placeholder}
            className={cn(
              'resize-none rounded-none border-0 text-[13px] shadow-none focus-visible:ring-0',
              minHeightClassName,
            )}
          />
        </>
      ) : (
        <ScrollArea viewportClassName={previewMaxHeightClassName}>
          <div className="px-3 py-2.5">
            {/* MessageBodyMarkdown renders null for whitespace-only input, so the empty state is ours. */}
            {text.trim() ? (
              <MessageBodyMarkdown>{text}</MessageBodyMarkdown>
            ) : (
              <p className="text-[13px] italic text-muted-foreground">Nothing to preview yet.</p>
            )}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
